import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { IScoreValues } from '../../../../app/@types/pill';
import { Score } from '../../../../app/myAudit/components/Score';
import { useTranslation } from 'react-i18next';
import { getMyAudit } from '../../../../requests/myAudit';
import { showErrorMessage } from '../../../../helpers/showMessage';
import {
  ButtonBlockWrapper,
  ScoreContainer,
  StyledMyAuditViewContainer,
  TabsWrapper,
} from './styled.MyAuditView';
import { ViewSkeleton } from '../../MyAuditSkeleton/ViewSkeleton';
import { HeaderContainer } from '../../../common/sharedStyledComponents/MyAuditGenericStyles.styled';
import { AppHeader } from '../../../common/sharedComponents/AppHeader';
import { ScoreTypes } from '../../../@types/genericTypes';
import { TabContainer } from '../../../common/sharedComponents/TabContainer';
import { MyAuditViewTabContent } from './MyAuditViewTabContent';
import { IMyAudit } from '../../../@types/myAudit';
import { ButtonLarge } from '../../../common/sharedComponents/buttons/ButtonLarge';

export function MyAuditView () {
  const [searchParams] = useSearchParams();
  const sectionId = Number(searchParams.get('sectionId'));
  const auditId = Number(searchParams.get('auditId')) || 0;
  const [selectedTabIndex, setSelectedTabIndex] = useState<number>();
  const [activeTabContent, setActiveTabContent] = useState<IMyAudit>();
  const [scoreList, setScoreList] = useState<IScoreValues[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    getMyAudit(auditId, 'view')
      .then((response) => {
        setActiveTabContent(response.data);
        const sectionIndex = response.data.myAuditSections.findIndex(
          (section) => section.id === sectionId
        );
        setSelectedTabIndex(sectionIndex === -1 ? 0 : sectionIndex);
      })
      .catch(() => showErrorMessage(t('COMMON.MY_AUDIT.VIEW.MESSAGE.ERROR')));
  }, []);

  useEffect(() => {
    if (selectedTabIndex === 0 || selectedTabIndex) {
      const calculatedScoreList = calculateScore();
      setScoreList(calculatedScoreList);
    }
  }, [selectedTabIndex]);

  const { t } = useTranslation();

  function scoreCountCalculator (type: ScoreTypes) {
    if (activeTabContent && (selectedTabIndex === 0 || selectedTabIndex)) {
      return activeTabContent.myAuditSections[
        selectedTabIndex
      ].standards.filter((standard) => standard.score === type).length;
    } else {
      return 0;
    }
  }

  function totalScoreCalculator () {
    const metStandards = scoreCountCalculator(1);
    const noMetStandards = scoreCountCalculator(0);
    const totalValidStandards = metStandards + noMetStandards;
    const totalScore = (metStandards / totalValidStandards) * 100 || 0;
    return `${totalScore.toFixed(2)}%`;
  }

  function calculateScore () {
    return [
      {
        key: 'Score',
        value: totalScoreCalculator(),
        keyValue: 'Score',
        backgroundColor: 'platinum',
      },
      {
        key: 'Met',
        value: scoreCountCalculator(1),
        keyValue: 'Met',
        backgroundColor: 'verdigris',
      },
      {
        key: 'No',
        value: scoreCountCalculator(0),
        keyValue: 'No Met',
        backgroundColor: 'redCrayola',
      },
      {
        key: 'NA',
        value: scoreCountCalculator(null),
        keyValue: 'N/A',
        backgroundColor: 'battleShipGray',
      },
    ];
  }

  const tabs =
    activeTabContent &&
    activeTabContent.myAuditSections.map((section) => {
      return {
        title: section.name,
        content: <MyAuditViewTabContent section={section} />,
      };
    });

  return (
    <>
      <HeaderContainer>
        <AppHeader title={t('COMMON.MY_AUDIT.TITLE')} />
      </HeaderContainer>
      <StyledMyAuditViewContainer>
        {!activeTabContent && <ViewSkeleton />}
        {activeTabContent && (
          <>
            <ScoreContainer>
              <Score scoreList={scoreList} />
            </ScoreContainer>
            <TabsWrapper>
              <TabContainer
                title={activeTabContent.templateName}
                tabs={tabs}
                selectedTabIndex={selectedTabIndex}
                handleTabSelection={setSelectedTabIndex}
              />
            </TabsWrapper>
            <ButtonBlockWrapper>
              <ButtonLarge
                onClick={() => navigate(-1)}
                title="Back"
                outlined={true}
                inverted={true}
              />
            </ButtonBlockWrapper>
          </>
        )}
      </StyledMyAuditViewContainer>
    </>
  );
}
